import { slugify } from "./slug";
import type { DemoBundle, Outcome, Step } from "./types";

type Paths = Pick<DemoBundle, "steps" | "outcomes">;
const byOrder = (a: Step, b: Step) => (a.order ?? a.orderIndex) - (b.order ?? b.orderIndex) || a.id.localeCompare(b.id);

/**
 * THE rule for which screens a path shows, and in what order. Everything that lists or counts screens (the
 * Studio, Paths, Preview, the published tour, the publish checks, analytics) goes through here, so they cannot
 * disagree about it.
 *
 * Every path has its own steps. The default path (null) is the steps marked `inDefault`, in their order; any other
 * path is the steps it lists, in its own order. Each step is on one list only (the same media can be on several,
 * as separate steps), so editing or deleting a step changes that path only.
 */
export function pathScreens(b: Paths, pathId: string | null): Step[] {
  if (!pathId) return b.steps.filter((s) => s.inDefault !== false).sort(byOrder);
  const path = b.outcomes.find((o) => o.id === pathId);
  if (!path) return [];
  const byId = new Map(b.steps.map((s) => [s.id, s]));
  return (path.stepIds ?? []).map((id) => byId.get(id)).filter((s): s is Step => !!s);
}

/** How many of a path's routing tags the viewer has. */
export function tagMatches(path: Pick<Outcome, "tags">, tags: Set<string>): number {
  return new Set((path.tags ?? []).map((t) => t.trim()).filter((t) => tags.has(t))).size;
}

/**
 * Which path a viewer with these tags (from their survey answers) is on: the path matching the most of them; on a tie,
 * the one higher in the list of paths; when no path matches any, the default path (null).
 */
export function routePath(outcomes: Outcome[], tags: Set<string> | string[]): string | null {
  const set = tags instanceof Set ? tags : new Set(tags);
  let best: Outcome | null = null;
  let bestScore = 0;
  for (const o of [...outcomes].sort((a, z) => a.orderIndex - z.orderIndex)) {
    const n = tagMatches(o, set);
    if (n > bestScore) { best = o; bestScore = n; }
  }
  return best?.id ?? null;
}

/** The path a step is on, as a name list (["Default path"], or the path's name; [] for a step on no path). */
export function pathsOf(b: Paths, stepId: string): string[] {
  const s = b.steps.find((x) => x.id === stepId);
  return [
    ...(s && s.inDefault !== false ? ["Default path"] : []),
    ...b.outcomes.filter((o) => (o.stepIds ?? []).includes(stepId)).map((o) => o.name),
  ];
}

/**
 * The path a link asks for (`?path=...`): a path's id, or its name as a slug ("reduce-cost"); "default" is the
 * default path (null). undefined = the link asks for none (or one that doesn't exist), so the survey decides.
 */
export function pathFromParam(outcomes: DemoBundle["outcomes"], param: string | null | undefined): string | null | undefined {
  const p = (param ?? "").trim();
  if (!p) return undefined;
  const wanted = slugify(p);
  if (wanted === "default" && !outcomes.some((o) => slugify(o.name) === "default")) return null;
  return outcomes.find((o) => o.id === p)?.id ?? outcomes.find((o) => slugify(o.name) === wanted)?.id ?? undefined;
}
